import { loadAppConfig, setupAppInstance } from '../config/app.config';
import { createApp, defineComponent, defineCustomElement } from 'vue';
import {
  addConnectionAemEnvironmentConnection,
  connectionName as aemEnvironmentConnectionName,
} from '@owc/connection-aem-environment';
import { initializeComponentRegistry } from '@owc/component-registry-connection';
import { initializeStore } from '@seamless/store';
import { createDataLoaderComponent } from '../components/loading/dataLoader';
import { AbstractRouterOptions } from '../router/types';
import { createWcWrapper } from './wc-wrapper.util';
import { Logger } from './logger.util';

export interface AemComponentConfig {
  routingEnabled: boolean;
  routerNamespace?: string;
  available: boolean;
  userSessionRequired: boolean;
}

export interface AemComponentData<TPayload = undefined> {
  config: AemComponentConfig;
  payload: TPayload;
}

export type AemEnvironmentVariables = {
  language: string;
  country: string;
  stage: string;
  runMode: 'AUTHOR' | 'PUBLISH';
};

export const isMockServer = () =>
  ['localhost', 'dcp-local.mercedes-benz.com'].includes(window.location.hostname) &&
  window.location.pathname.startsWith('/serve');

export const getEmhEnvironment = (stage: string) => {
  switch (stage) {
    case 'test':
      return 'dev-01';
    case 'pprod':
      return 'preprod';
    case 'prod':
      return 'prod';
    default:
      return 'int-01';
  }
};

const createTeleportTarget = (id = 'emh-modal-target') => {
  if (document.getElementById(id)) return;

  const modalTarget = document.createElement('div');

  modalTarget.id = id;
  document.body.appendChild(modalTarget);
};

export const defineAEMCustomElement = async ({
  componentTag,
  MainComponent,
  LoadingComponent,
  loadingComponentProps,
  ErrorComponent,
  errorComponentProps,
  routerOptions,
  loadAppDataFn,
}: {
  componentTag: string;
  MainComponent: ReturnType<typeof defineComponent>;
  LoadingComponent?: ReturnType<typeof defineComponent>;
  loadingComponentProps?: Record<string, unknown>;
  ErrorComponent?: ReturnType<typeof defineComponent>;
  errorComponentProps?: Record<string, unknown>;
  routerOptions?: (aemPayload: any) => AbstractRouterOptions;
  loadAppDataFn?: () => Promise<unknown>;
}) => {
  // eslint-disable-next-line vue/require-name-property
  const app = createApp({});

  const dataLoadFn = async (aemComponentData?: AemComponentData): Promise<void> => {
    if (!aemComponentData) {
      throw new Error('missing AEM component data');
    }

    const aemEnvVars = await getAemEnvironmentVariables();
    const { language, stage, runMode } = aemEnvVars;

    // Use language as a country code only in case of the AEM Antarctica test market.
    // E.g.: it_AQ -> it_IT, de_AQ _> de_DE
    const country = aemEnvVars.country === 'AQ' ? language.toUpperCase() : aemEnvVars.country;

    const emhEnv = getEmhEnvironment(stage.toLowerCase());
    let appConfig = await loadAppConfig(emhEnv, country, language);

    // Extend static app config with some runtime values
    appConfig = {
      ...appConfig,
      runMode,
      componentTag,
      tracking: {
        ...appConfig.tracking,
        id: 'emh',
        version: '',
        type: appConfig.tracking.assortmentType,
        parentApplicationId: 'aem',
      },
      aemApp: {
        ...(appConfig.aemApp as any),
        namespace: aemComponentData.config.routerNamespace,
      },
    };

    // TODO: make router config completely optional
    const routerConfig = routerOptions?.(aemComponentData.payload) ?? { routes: [] };

    await setupAppInstance('emh', appConfig, routerConfig, app);

    if (loadAppDataFn) {
      await loadAppDataFn();
    }
  };

  createTeleportTarget();

  const CustomComponent = createDataLoaderComponent({
    LoadingComponent,
    loadingComponentProps,
    MainComponent,
    ErrorComponent,
    errorComponentProps,
    dataLoadFn,
  });

  const WrappedCustomComponent = createWcWrapper(CustomComponent, app);
  const CustomElement = defineCustomElement(WrappedCustomComponent);

  try {
    window.customElements.define(componentTag, CustomElement);
  } catch (err) {
    console.log(err);
  }
};

export const buildSeamlessRoute = (route: { link?: string; path?: string }): string => {
  if (!route.link && !route.path) {
    Logger.warn('buildSeamlessRoute: No link or path provided');
  }

  if (route.link) {
    // Links defined via AEM dialog options are missing the .html suffix so we have to add it manually
    if (!route.link.endsWith('.html')) {
      return `${route.link}.html`;
    } else {
      return route.link;
    }
  }

  return route.path as string;
};

// For the Seamless/AEM pages the PDP routes should be adjusted to the pathname property,
// which is currently not supported directly by Seamless
// E.g. product-detail.html/B-250-E_0-20132976_DE_e8bf7008
// This was a SEO requirement and is considered a hack, as it requires special handling in some other places.
export const adjustPDPUrl = (url: URL, namespace: string): URL => {
  // On mock server we don't need to do the hacky stuff below
  // because we can access the product code via simple query parameters.
  if (isMockServer()) {
    return url;
  }

  const adjustedURL = new URL(url.toString());
  const productCode: string | null = adjustedURL.searchParams.get(`${namespace}code`);

  // If product code is in the searchParams -> remove it from there and add it to the pathname
  if (productCode) {
    adjustedURL.searchParams.delete(`${namespace}code`);
    // if there's already slash at the end, don't add another slash
    adjustedURL.pathname = adjustedURL.pathname.endsWith('/')
      ? `${adjustedURL.pathname}${productCode}`
      : `${adjustedURL.pathname}/${productCode}`;
  }

  return adjustedURL;
};

export const getAemComponentData = <TPayload = undefined>(tagName: string): Promise<AemComponentData<TPayload>> => {
  return new Promise((resolve, reject) => {
    const elem = document.querySelector(tagName);

    if (!elem) {
      reject(`element with tag name '${tagName}' not found`);
    }

    const componentId = elem?.getAttribute('component-id');

    if (!componentId) {
      reject('missing datajson attribute');
    }

    const componentRegistry = initializeComponentRegistry();

    const handler = (data: any) => {
      resolve(data);
      setTimeout(() => {
        componentRegistry.unsubscribe(componentId as string, handler);
      }, 0);
    };

    componentRegistry.subscribe(componentId as string, handler);
  });
};

export const getAemEnvironmentVariables = (): Promise<AemEnvironmentVariables> =>
  new Promise((resolve) => {
    const store = initializeStore();

    addConnectionAemEnvironmentConnection().finally(() => {
      store.once(aemEnvironmentConnectionName, (state) => {
        resolve(state);
      });
    });
  });
